import React, { useEffect, useState, useCallback, useRef } from "react";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { Toaster } from "sonner";
import { AuthContext } from "./context/AuthContextProvider";
import { SocketContext } from "./context/SocketContextProvider";
import loader from "./assets/images/logos/spandan-logo.svg";
import AppRoutes from "./routes/AppRoutes";
import {
  setsession,
  setSessionExpiredHandler,
  resetSessionExpiredGuard,
} from "./utils/axios";
import { VerifyToken, Registerfcmtoken, useLogout } from "./api/auth";
import { useGetCentres, useGetPeoples } from "./api/settings";
import { VAPID_KEY, messagingReady, swRegistration } from "./firebase";
import { getToken, onMessage } from "firebase/messaging";
import { connectSocket, disconnectSocket } from "./socketService";
import { toast } from "sonner";
import sirenURL from "./assets/audio/siren.mp3";
import ringURL from "./assets/audio/ring.mp3";
// import ThemeProvider from './theme';
// import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
// import { LocalizationProvider } from '@mui/x-date-pickers';
import { platforms } from "./components/WebUsageStats";
import NoInternet from "./pages/noInternet";
import notify from "./services/notification.service";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import useMobileLayout from "./hooks/useMobileLayout";
import {
  ANALYTICS_EVENTS,
  LOGOUT_TRIGGER,
  clearAnalyticsUser,
  logAnalyticsEvent,
  resetScreenTracking,
  setAnalyticsUser,
} from "./analytics";
import useScreenTracking from "./analytics/useScreenTracking";

function App() {
  const isMobile = useMobileLayout();
  useScreenTracking();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [onReceivePushNotification, setOnReceivePushNotification] =
    useState(false);
  const [isLogin, setIsLogin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [socketMessage, setSoketMessage] = useState(null);
  const [user, setUser] = useState(null);
  const [loginData, setLoginData] = useState(null);
  const [all_centres, setAllCentres] = useState([]);
  const [allCenterIds, setAllCenterIds] = useState([]);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [filter, setFilter] = useState(null);
  const navigate = useNavigate();
  const { centres, pagination: centerCount } = useGetCentres(filter || {}, isLogin); // only used after login
  const { pagination: peopleCount } = useGetPeoples(filter || {}, isLogin);
  // const [open, setOpen] = useState(isMobile ? true : false);
  const [open, setOpen] = useState(() => {
    const saved = localStorage.getItem("sidebar_open");

    if (saved !== null) return JSON.parse(saved);

    // default behavior
    return !isMobile; // desktop = open, mobile = closed
  });
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const audioSetupDoneRef = useRef(false);
  // Signed out when the backend retires this session (an admin changed this
  // member's password). Held in a ref because the push listener is registered
  // once on mount and must not capture a stale logout.
  const sessionExpiredRef = useRef(null);

  // audio ref and gesture listener ref
  const notificationAudioRef = useRef(null);
  const ringAudioRef = useRef(null);
  const pendingGestureRef = useRef(false);
  const gestureHandlerRef = useRef(null);

  // On mobile the drawer is an overlay driven by `mobileOpen`; `open` is the
  // desktop rail state (expanded vs. mini) and must not be touched from mobile.
  const toggleDrawer = useCallback(() => {
  if (isMobile) {
    setMobileOpen((prev) => !prev);
  } else {
    setOpen((prev) => {
      const next = !prev;
      localStorage.setItem("sidebar_open", JSON.stringify(next));
      return next;
    });
  }
}, [isMobile]);

  const handleMobileDrawerToggle = useCallback(() => {
    setMobileOpen((prev) => !prev);
  }, []);

  const closeMobileDrawer = useCallback(() => {
    setMobileOpen(false);
  }, []);

  // Remember the desktop rail state (the initial state above reads it back).
  // useEffect(() => {
  //   if (!isMobile) {
  //     localStorage.setItem("sidebar_open", JSON.stringify(open));
  //   }
  // }, [open, isMobile]);

  // Growing past the mobile breakpoint (rotation / resize) hands over to the
  // permanent drawer — drop any leftover overlay so it can't reappear.
  useEffect(() => {
    if (!isMobile) setMobileOpen(false);
  }, [isMobile]);

  // ---------- Combined audio setup + onMessage registration ----------
  useEffect(() => {
    //  Prevent double execution in React 18 StrictMode
    if (audioSetupDoneRef.current) return;
    audioSetupDoneRef.current = true;

    try {
      const audio = document.createElement("audio");
      audio.id = "fcm-notification-audio-siren";
      audio.preload = "auto";
      audio.playsInline = true;
      audio.src = sirenURL;
      audio.volume = 0.9;
      audio.muted = false;
      audio.style.display = "none";
      audio.setAttribute("aria-hidden", "true");

      const audioRing = document.createElement("audio");
      audioRing.id = "fcm-notification-audio-ring";
      audioRing.preload = "auto";
      audioRing.playsInline = true;
      audioRing.src = ringURL;
      audioRing.volume = 0.9;
      audioRing.muted = false;
      audioRing.style.display = "none";
      audioRing.setAttribute("aria-hidden", "true");

      const onError = (e) =>
        console.error("Notification audio failed to load/play:", e);
      const onReady = () =>
        console.info("Notification audio ready.");

      audio.addEventListener("error", onError);
      audio.addEventListener("canplaythrough", onReady);
      audioRing.addEventListener("error", onError);
      audioRing.addEventListener("canplaythrough", onReady);

      document.body.appendChild(audio);
      document.body.appendChild(audioRing);

      notificationAudioRef.current = audio;
      ringAudioRef.current = audioRing;

      audio.load();
      audioRing.load();

      //  Register FCM listener ONCE, and only once Cloud Messaging reports it
      //  is usable. Where it is not - the dev server opened over a LAN address,
      //  a private window - `messagingReady` resolves to null and push is simply
      //  skipped; the audio elements above are still set up either way.
      let unsubscribe;
      let cancelled = false;

      const handlePushMessage = (payload) => {

        // An admin changed this account's password, so the token this tab holds
        // is dead. Handled before anything else and returned from: this is not a
        // notification to read, it is a sign-out - and a tab sitting idle would
        // otherwise keep showing a signed-in app until the next API call.
        if (payload?.data?.activity === "PASSWORD_CHANGED") {
          sessionExpiredRef.current?.(payload.notification?.body);
          return;
        }

        // if (payload?.data?.interpretation_report_status === "critical") {
        //   toast.warning(payload.notification?.title || "Critical Alert!", {
        //     description: payload.notification?.body,
        //     position: "top-center",
        //   });
        if (payload?.data?.interpretation_report_status === "critical") {
          notify({
            message: payload.notification?.title || "Critical Alert!",
            type: "warning",
            options: {
              description: payload.notification?.body,
              position: "top-center",
            },
          });
          playNotificationSound(notificationAudioRef.current);
        } else if (payload?.data?.activity === "CALL_JOIN") {
          notify({
            message: payload.notification?.title || "Incoming Call",
            type: "success",
            options: {
              description: payload.notification?.body,
              position: "top-center",
            },
          });
          playNotificationSound(ringAudioRef.current);
        } else {
          notify({
            message: payload.notification?.title || "New Notification",
            type: "success",
            options: {
              description: payload.notification?.body,
              position: "top-center",
            },
          });
        }

        setOnReceivePushNotification(payload);
      };

      messagingReady.then((fcm) => {
        if (fcm && !cancelled) {
          unsubscribe = onMessage(fcm, handlePushMessage);
        }
      });

      return () => {
        cancelled = true;
        try {
          unsubscribe?.();
        } catch { }
      };
    } catch (err) {
      console.error("Audio + FCM setup error:", err);
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success("Back online");
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast.error("No internet connection");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);



  // play sound with user-gesture fallback
  const playNotificationSound = (audio) => {
    // const audio = notificationAudioRef.current;

    // If element exists, use it. Otherwise fallback to transient Audio().
    if (audio) {
      try {
        audio.currentTime = 0;
      } catch (e) {
        // ignore setting currentTime if not supported
      }

      audio
        .play()
        .then(() => {
          // played successfully
          pendingGestureRef.current = false;
          console.info("Played notification audio using DOM audio element.");
        })
        .catch((err) => {
          // play blocked (likely NotAllowedError). Attach one-time user gesture handler.
          if (pendingGestureRef.current) return;
          pendingGestureRef.current = true;

          const gestureHandler = () => {
            try {
              audio
                .play()
                .catch((playErr) => {
                  console.warn("Play after gesture failed:", playErr);
                })
                .finally(() => {
                  // cleanup after first attempt
                  pendingGestureRef.current = false;
                  if (gestureHandlerRef.current) {
                    window.removeEventListener(
                      "click",
                      gestureHandlerRef.current
                    );
                    window.removeEventListener(
                      "keydown",
                      gestureHandlerRef.current
                    );
                    window.removeEventListener(
                      "touchstart",
                      gestureHandlerRef.current
                    );
                    gestureHandlerRef.current = null;
                  }
                });
            } catch (innerErr) {
              console.warn("Gesture handler error:", innerErr);
            }
          };

          // save handler to ref so we can remove later
          gestureHandlerRef.current = gestureHandler;

          // Listen for typical user interaction events; these are removed as soon as handler runs
          window.addEventListener("click", gestureHandler, { once: true });
          window.addEventListener("keydown", gestureHandler, { once: true });
          window.addEventListener("touchstart", gestureHandler, { once: true });

          console.info(
            "Autoplay blocked. Will attempt to play audio on the next user interaction."
          );
        });

      return;
    }

    // Fallback: transient Audio() (useful if element wasn't created)
    try {
      const tmp = new Audio(sirenURL);
      tmp.preload = "auto";
      tmp
        .play()
        .then(() =>
          console.info("Played notification audio using transient Audio().")
        )
        .catch((err) => {
          console.warn("Transient Audio play failed:", err);
        });
    } catch (err) {
      console.error("Fatal audio play error:", err);
    }
  };

  // Try to prime the audio after the first user gesture so autoplay is more reliable
  useEffect(() => {
    const onFirstGesture = async () => {
      const audio = notificationAudioRef.current;
      if (!audio) return;
      try {
        // small play/pause to unlock autoplay on some browsers
        await audio.play();
        audio.pause();
        audio.currentTime = 0;
        console.info("Audio primed for autoplay via user gesture.");
      } catch (e) {
        console.warn("Priming audio failed:", e);
      } finally {
        window.removeEventListener("click", onFirstGesture);
        window.removeEventListener("keydown", onFirstGesture);
        window.removeEventListener("touchstart", onFirstGesture);
      }
    };

    window.addEventListener("click", onFirstGesture, { once: true });
    window.addEventListener("keydown", onFirstGesture, { once: true });
    window.addEventListener("touchstart", onFirstGesture, { once: true });

    return () => {
      window.removeEventListener("click", onFirstGesture);
      window.removeEventListener("keydown", onFirstGesture);
      window.removeEventListener("touchstart", onFirstGesture);
    };
  }, []);

  // Optional test timer — uncomment to test playNotificationSound after 5s
  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     console.log("Running test: calling playNotificationSound()");
  //     playNotificationSound();
  //   }, 5000);
  //   return () => clearTimeout(timer);
  // }, []);

  const requestPermission = async () => {
    try {
      // Null wherever push is unsupported - nothing to ask permission for.
      const fcm = await messagingReady;
      if (!fcm) return;

      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        // Get FCM token
        const token = await getToken(fcm, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: await swRegistration,
        });

        const response = await Registerfcmtoken({ fcmToken: token });

        // TODO: Send this token to your backend server to send notifications
      } else {
        // permission denied or default
      }
    } catch (error) {
      console.error("requestPermission error:", error);
    }
  };

  // On load, validate token if available (keeps requestPermission)
  useEffect(() => {
    const init = async () => {
      const data = JSON.parse(localStorage.getItem("@auth_login"));
      if (data?.token) {
        await setsession(data.token);
        await VerifyProfileToken(data.token);
      } else {
        setLoading(false);
      }
    };

    init();
    requestPermission(); // request permission on mount (you may want to move this to after login)
  }, []); // run once

  // Prepare centre options once centres are fetched
  useEffect(() => {
    if (!user) return;

    const newCentres = (centres || [])
      .filter((centre) => centre?.name)
      .map((centre) => ({
        label: centre.name,
        value: centre?.id,
        logo: centre.logo,
        type: centre.type,
        status: centre.status,
        fullAddress: centre?.address?.fulladdress || "",
        latitude: centre?.address?.location?.coordinates?.[1],
        longitude: centre?.address?.location?.coordinates?.[0],
        canInterpretReport: centre?.canInterpretReport ?? false,
        spokeCenters: centre?.spokeCenters,
      }));

    setAllCentres(newCentres);
    localStorage.setItem(
      "@centre_stats",
      JSON.stringify({ totalCount: centerCount?.total ?? 0 })
    );

    localStorage.setItem(
      "@people_stats",
      JSON.stringify({ totalCount: peopleCount?.total ?? 0 })
    );

    // single array of ALL ids (centre + spoke)
    const allIds = newCentres.flatMap((centre) => [
      centre.value,
      ...(centre.spokeCenters?.map((s) => s.id) || []),
    ]);
    setAllCenterIds(allIds);

    const found = newCentres.find((c) => c.value === user?.selectedCenter);
    setSelectedCenter(found);
    setLoading(false); // Always set loading to false
  }, [centres, user, peopleCount]);

  const onSocketMessage = (event) => {
    setSoketMessage(JSON.parse(event));
  };

  const login = useCallback(
    async (user, token, redirect = true) => {
      const data = { token, data: user };
      localStorage.setItem("@auth_login", JSON.stringify(data));

      setsession(token);
      // This tab may have been signed out by an expired session a moment ago;
      // arm the interceptor again so a later expiry is not swallowed as a repeat.
      resetSessionExpiredGuard();
      setUser(user);
      setLoginData(user);
      setIsLogin(true);
      setFilter({ reload: Math.floor(Math.random() * 100) + 1 });

      // Identity only — the login_success event itself is fired by the screen
      // that performed the login, because that is the only place that knows
      // which method was used. This runs for restored sessions and onboarding
      // hand-offs too, which must set properties without counting as a login.
      setAnalyticsUser(user);
      // Decide target once
      const target = parseInt(user.stage) > 0 ? `/onboarding/${user.stage}` : "/dashboard";
      let shouldRedirect = redirect;
      if (user.stage > 0) {
        shouldRedirect = true;
      }

      // Navigate immediately (so user doesn't see landing page)
      if (shouldRedirect) {
        navigate(target, { replace: true });
      }

      // Fire-and-forget notification + FCM registration so navigation isn't delayed
      (async () => {
        try {
          const fcm = await messagingReady;
          if (!fcm) return;

          const permission = await Notification.requestPermission();
          if (permission === "granted" || permission === "denied") {
            const fcmToken = await getToken(fcm, {
              vapidKey: VAPID_KEY,
              serviceWorkerRegistration: await swRegistration,
            });
            const oldFcmToken = localStorage.getItem("fcmToken");
            if (oldFcmToken !== fcmToken) {
              const resFcm = await Registerfcmtoken({ fcmToken, oldFcmToken });
              if (resFcm.success) {
                localStorage.setItem("fcmToken", fcmToken);
                localStorage.setItem("fcmToken", oldFcmToken);
              }
            }
          }
        } catch (err) {
          // optional: console.error(err)
        }
      })();

      // Enable socket connection (can run synchronously)
      connectSocket(token, onSocketMessage);
    },
    [navigate]
  );

  const logout = useCallback(async (trigger) => {
    // logout is handed straight to consumers through context, so an onClick={logout}
    // would arrive here with a click event. Only trust a string.
    logAnalyticsEvent(ANALYTICS_EVENTS.LOGOUT, {
      trigger: typeof trigger === "string" ? trigger : LOGOUT_TRIGGER.MANUAL,
    });

    const fcmTken = localStorage.getItem("fcmToken");
    await useLogout({ fcmToken: fcmTken, platform: "browser" });
    localStorage.clear();
    sessionStorage.clear();
    setsession("");
    setIsLogin(false);
    setUser(null);
    setLoginData(null);
    setSelectedCenter(null);
    setAllCentres([]);
    setFilter(null);
    setLoading(false);
    disconnectSocket();

    // Without this the next user on a shared browser inherits the previous
    // user_id, and their events join onto the wrong person in reporting.
    clearAnalyticsUser();
    resetScreenTracking();
  }, []);

  /**
   * Sign this tab out because the session is no longer valid server-side - an
   * admin reset this member's password from Manage Members, which retires every
   * token issued before the change.
   *
   * Reached three ways, and all three land here so the outcome is identical:
   * the 401 + SESSION_EXPIRED any API call now gets, the reverify on reload,
   * and the push notification (the only one that works on an idle tab).
   */
  const handleSessionExpired = useCallback(
    (message) => {
      notify({
        message: message || "Your session has ended. Please log in again.",
        type: "warning",
        options: { position: "top-center" },
      });
      // No navigate() here, the same as a manual logout: clearing is_login is
      // what sends RequireAuth to /loginpassword, and routing it by hand would
      // only fight that redirect.
      logout(LOGOUT_TRIGGER.SESSION_EXPIRY);
    },
    [logout]
  );

  // The push listener reads this through a ref, and axios has no access to the
  // tree at all, so both are pointed at the handler here.
  useEffect(() => {
    sessionExpiredRef.current = handleSessionExpired;
    setSessionExpiredHandler(handleSessionExpired);
  }, [handleSessionExpired]);

  // A push delivered while this tab is in the background goes to the service
  // worker, not to onMessage - it forwards the force-logout here so a tab left
  // open on another window still signs out.
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    const onWorkerMessage = (event) => {
      if (event.data?.type === "PASSWORD_CHANGED") {
        sessionExpiredRef.current?.(event.data.message);
      }
    };

    navigator.serviceWorker.addEventListener("message", onWorkerMessage);
    return () =>
      navigator.serviceWorker.removeEventListener("message", onWorkerMessage);
  }, []);

  const VerifyProfileToken = useCallback(
    async (token) => {
      try {
        const response = await VerifyToken({ token });
        if (response?.success) {
          login(response.data, response.token, false);
        } else if (response?.code === "SESSION_EXPIRED") {
          // Reverify refused to re-issue a token because the password changed
          // while this tab held the old one. Clearing the stored session here is
          // what makes the reload land on the login screen; logout() also drops
          // the boot loader, which nothing else on this branch would.
          handleSessionExpired(response.message);
        } else {
          // logout();
        }
      } catch (error) {
        console.error("Error verifying profile:", error);
        // logout();
      }
    },
    [login, handleSessionExpired]
  );

  // Initial loader

  if (!isOnline) {
    return (
      <NoInternet />
    );
  }

  if (loading) {
    return (
      <div className="preloader">
        <img src={loader} alt="Loading..." />
        <span className="loader-70" />
      </div>
    );
  }

  return (
    <>
      {/* Installable-app prompt. Renders nothing unless the browser says the app
          can be installed and the user has not dismissed it recently. */}
      <PWAInstallPrompt />
      {/* <LocalizationProvider dateAdapter={AdapterDayjs}>
     <ThemeProvider> */}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <SocketContext.Provider value={{ socketMessage }}>
          <AuthContext.Provider
            value={{
              login,
              logout,
              is_login: isLogin,
              user,
              role: loginData,
              all_centres,
              allCenterIds,
              selectedCenter,
              setFilter,
              loading,
              open,
              handleDrawerToggle: toggleDrawer,
              handleMobileDrawerToggle,
              closeMobileDrawer,
              mobileOpen,
              isMobileLayout: isMobile,
              onReceivePushNotification,
            }}
          >
            <AppRoutes />
          </AuthContext.Provider>
        </SocketContext.Provider>
      </LocalizationProvider>

      <Toaster
        expand={true}
        position="bottom-center"
        toastOptions={{
          style: {
            backgroundColor: "#ffffff",
            color: "#222",
            padding: "16px",
            display: "flex",
            alignItems: "center",
            gap: "20px",
          },
        }}
        icons={{
          success: (
            <Icon
              icon="lets-icons:check-fill"
              width="30"
              height="30"
              style={{ color: "green" }}
            />
          ),
          error: (
            <Icon
              icon="material-symbols:error"
              width="30"
              height="30"
              style={{ color: "#e72020" }}
            />
          ),
          warning: (
            <Icon
              icon="material-symbols:warning"
              width="30"
              height="30"
              style={{ color: "#f5a623" }} // yellowish orange
            />
          ),
        }}
      />
      {/* <Toaster
  position="bottom-center"
  richColors
/> */}
      {/* </ThemeProvider>
      </LocalizationProvider> */}
    </>
  );
}

export default App;
